import { NormalizedCacheObject } from '@apollo/client'
import { NextComponentType, NextPageContext } from 'next'
import NextApp, { AppContext, AppInitialProps } from 'next/app'
import { CookiesProvider, Cookies as ReactCookies } from 'react-cookie'
import { StyleSheetManager } from 'styled-components'
import Cookies from 'universal-cookie'

import {
  MediaContextProvider,
  ToplevelFigsThemeProvider,
  isPropValid,
} from '@syconium/little-miss-figgy'
import {
  ApolloClientProfile,
  ContextualizedClientFactory,
  ServiceFilterPairs,
  ShopLocalizationContext,
  buildContextForWebsitePageView,
  currencyKey,
  initializeClientFactory,
  isBrowser,
  localeKey,
  nameKey,
  regionKey,
  supportedRegions,
} from '@syconium/weeping-figs'

import { PreviewPanelDialog } from '../../../app/_components/dialogs/PreviewPanelDialog/PreviewPanelDialog'
import { cookieKeys } from '../../../app/_config/Cookies.config'
import { headerKeys } from '../../../app/_config/Headers.config'
import { convertRequestToPreviewDirectives } from '../../../app/_config/PreviewDirectives.config'
import { AuthenticationProvider } from '../../../app/_providers/AuthenticationProvider.client'
import { CartProvider } from '../../../app/_providers/CartProvider.client'
import { CheckoutClientProvider } from '../../../app/_providers/CheckoutClientProvider.client'
import { ConsentPolicyProvider } from '../../../app/_providers/ConsentPolicyProvider.client'
import { ExperimentationProvider } from '../../../app/_providers/ExperimentationProvider.client'
import { FavoritesProvider } from '../../../app/_providers/FavoritesProvider.client'
import { GraphqlClientsProvider } from '../../../app/_providers/GraphqlClientsProvider.client'
import { LocalizationProvider } from '../../../app/_providers/LocalizationProvider.client'
import { LoopProvider } from '../../../app/_providers/LoopProvider.client'
import { OrganizationProvider } from '../../../app/_providers/OrganizationProvider.client'
import { PersonalizationProvider } from '../../../app/_providers/PersonalizationProvider.client'
import { PreviewDirectivesProvider } from '../../../app/_providers/PreviewDirectivesProvider.client'
import { PreviewPanelProvider } from '../../../app/_providers/PreviewPanelProvider.client'
import { SessionProvider } from '../../../app/_providers/SessionProvider.client'
import { TrackingProvider } from '../../../app/_providers/TrackingProvider.client'
import {
  TranslationProvider,
  type Translations,
} from '../../../app/_providers/TranslationProvider.client'
import { UserProvider } from '../../../app/_providers/UserProvider.client'
import { INCLUDE_UNAVAILABLE_PRODUCTS, PREVIEW } from '../../../brunswick/constants'
import { FixturesContextProvider } from '../../../brunswick/containers/fixtures'
import { NavContainer } from '../../../brunswick/containers/NavContainer'
import { GlobalStyles } from '../../../brunswick/styles/global'
import { AccentColorProvider } from '../../../containers/AccentColorContainer'
import { EmailCaptureFormProvider } from '../../../containers/EmailCaptureFormContainer'
import { SearchOverlayContextProvider } from '../../../containers/SearchOverlayContext'
import { WithResponseCacheControl, conditionallySetCacheControl } from '../../../lib/response-cache'
import { MagnoliaRequest } from '../../../lib/server-middleware/MagnoliaRequest'
import { ContextualHead } from '../../chrome/contextual-head/ContextualHead'
import { Interstitial } from '../../chrome/Interstitial/Interstitial'
import { ScrollRestorer } from '../../chrome/ScrollRestorer/ScrollRestorer'
import { WithLayoutConfig, getLayoutConfigForPageView } from '../../layouts/layouts'

import type { JSX } from 'react'

// Items needed for the MagnoliaApp component to render and provide context.
// These get serialized into the HTML for hydration client-side.
type MagnoliaAppInitialPropsExtensions = {
  cookies?: Record<string, unknown>
  apolloCaches?: { [key: string]: NormalizedCacheObject }
  translations: Translations
  shopLocalizationContext: ShopLocalizationContext
  apolloClientFactory: ContextualizedClientFactory
  requestIncludedLocalizationPathParam: boolean
  userAgent: string | undefined
}

// Items we want to make available for all pages to use in their getInitialProps functions.
interface MagnoliaPageContextExtensions {
  shopLocalizationContext: ShopLocalizationContext
  apolloClientFactory: ContextualizedClientFactory
}

// Items we want to enforce our page components to implement
type MagnoliaPageExtensions = WithLayoutConfig

// Items we want to enforce our page's getInitialProps functions to return
type MagnoliaPageInitialPropsExtensions = WithResponseCacheControl

// Put it all together into the types for implementing a MagnoliaPage component
export type MagnoliaPageInitialProps<IP = {}> = IP & MagnoliaPageInitialPropsExtensions
export type MagnoliaPageContext = NextPageContext & MagnoliaPageContextExtensions
export type MagnoliaPage<P, IP = P> = NextComponentType<
  MagnoliaPageContext,
  MagnoliaPageInitialProps<IP>,
  P
> &
  MagnoliaPageExtensions
export type MagnoliaAppInitialProps = MagnoliaAppInitialPropsExtensions & AppInitialProps

function extractApolloClientCache(apolloClientFactory: ContextualizedClientFactory) {
  // TODO: Fix this auto-generated suppression.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const clients = apolloClientFactory.clients() ?? []
  const apolloCaches: { [key: string]: NormalizedCacheObject } = {}
  clients.forEach(([k, client]) => {
    apolloCaches[k] = client.cache.extract() as NormalizedCacheObject
  })
  return apolloCaches
}

// A little tricky to read.
//
// named browserClientFactory to indicate that in the server context this always remains
// null.  In the server runtime, the factory has been instantiated by middleware and is acquired
// from the request.
//
// On the browser, we want to maintain clients for the lifetime of the browser session, so we
// ensure and use this.
//
// Server: One apollo client factory context PER REQUEST
// Client: One apollo client factory context PER BROWSER SESSION
//
// Use the default provided context specific cache as there is only one context
// ever created on browser.
let browserClientFactory: ContextualizedClientFactory | null = null

const clientSideApolloClientFactory = initializeClientFactory('/')

function runtimeSpecificApolloClientFactory(req?: MagnoliaRequest) {
  // Server side the client is placed on request already
  if (req) {
    return req.apolloClientFactory
  }

  // Client side transitions need to build it again
  return (
    browserClientFactory ??
    (browserClientFactory = clientSideApolloClientFactory.createContext(
      runtimeSpecificShopLocalizationContext(req),
      {
        customerTier: 'web',
      }
    ))
  )
}

function runtimeSpecificShopLocalizationContext(req?: MagnoliaRequest) {
  // Server side gets context from middleware which places it on the request
  if (req) {
    return req.shopLocalizationContext
  }

  // Client-side transitions build context from cookies
  const ctx = buildContextForWebsitePageView({
    cookies: new Cookies(),
  })

  return ctx
}

async function getLayoutsRequiredData(
  MagnoliaPage: MagnoliaPage<MagnoliaPageInitialProps>,
  magnoliaPageContext: MagnoliaPageContext
) {
  const { preloadDataForLayout } = getLayoutConfigForPageView({
    query: magnoliaPageContext.query,
    pageComponent: MagnoliaPage,
  })
  await preloadDataForLayout?.(magnoliaPageContext)

  // We don't return anything, it is just priming Apollo Cache
  return
}

async function getTranslationsFromMagnoliaFiles(
  magnoliaRequest: MagnoliaRequest | undefined,
  magnoliaPageContext: MagnoliaPageContext
): Promise<Translations> {
  // It is not recommended to call APIs here. But if we don't, we would need to include these fixtures
  // in our client-side bundles. We want to use the domain we are running on, but remove the port if we are
  // not localhost.
  const baseUrl = magnoliaRequest
    ? `http://${magnoliaRequest.headers.host}`.replace(/\.com\:[0-9]+?$/, '.com')
    : ''

  const translationsResp = await fetch(
    `${baseUrl}/api/static/i18n/${magnoliaPageContext.shopLocalizationContext.locale}`
  )

  const fixtureParsingTracingSpan = magnoliaRequest?.tracer
    ? magnoliaRequest.tracer.startSpan('fixtures.parsing', {
        childOf: magnoliaRequest.tracer.scope().active() ?? undefined,
      })
    : undefined
  const translations = (await translationsResp.json()) as Translations
  fixtureParsingTracingSpan?.finish()

  return translations
}

export class MagnoliaApp extends NextApp<MagnoliaAppInitialProps> {
  // This method is a little bit tricky
  //
  // On server: it runs for every request
  // On client: it runs for every TRANSITION and is not run on first SSR result. Basically it runs when the router path changes (also not ran with router query changes)
  //
  static async getInitialProps(appContext: AppContext): Promise<MagnoliaAppInitialProps> {
    // This is the request that was enhanced by our Express middleware (server.ts)
    const magnoliaRequest = appContext.ctx.req as MagnoliaRequest | undefined

    // If we are handling a request on the server, augment DataDog tracing
    const getInitialPropsTracingSpan = magnoliaRequest?.tracer
      ? magnoliaRequest.tracer.startSpan('app.getInitialProps', {
          childOf: magnoliaRequest.tracer.scope().active() ?? undefined,
          tags: {
            // TODO: Fix this auto-generated suppression.
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            [localeKey]: magnoliaRequest.shopLocalizationContext?.locale,
            // TODO: Fix this auto-generated suppression.
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            [regionKey]: magnoliaRequest.shopLocalizationContext?.region,
            // TODO: Fix this auto-generated suppression.
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            [currencyKey]: magnoliaRequest.shopLocalizationContext?.currency,
            // TODO: Fix this auto-generated suppression.
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            [nameKey]: magnoliaRequest.shopLocalizationContext?.name,
          },
        })
      : undefined

    // Use that request to create an enhanced context to pass to each page's getInitialProps function
    const magnoliaPageContext = MagnoliaApp.enhanceMagnoliaPageContext(
      appContext.ctx,
      magnoliaRequest
    )

    // Mark the page for enhancement with our type
    const MagnoliaPage = appContext.Component as MagnoliaPage<MagnoliaPageInitialProps>

    // Grab cookies from request if we are on the server
    const cookies = magnoliaRequest ? magnoliaRequest.universalCookies.getAll() : undefined

    const pageInitialPropsPromise = magnoliaRequest?.tracer
      ? magnoliaRequest.tracer.trace('page.getInitialProps', () =>
          NextApp.getInitialProps(appContext)
        )
      : NextApp.getInitialProps(appContext)

    const translationsPromise = magnoliaRequest?.tracer
      ? magnoliaRequest.tracer.trace('app.getTranslations', () =>
          getTranslationsFromMagnoliaFiles(magnoliaRequest, magnoliaPageContext)
        )
      : getTranslationsFromMagnoliaFiles(magnoliaRequest, magnoliaPageContext)

    const layoutDataPromise = magnoliaRequest?.tracer
      ? magnoliaRequest.tracer.trace('layout.getLayoutsRequiredData', () =>
          getLayoutsRequiredData(MagnoliaPage, magnoliaPageContext)
        )
      : getLayoutsRequiredData(MagnoliaPage, magnoliaPageContext)

    // Await all async items that were triggered.
    const translations = await translationsPromise
    const pageInitialProps = await pageInitialPropsPromise
    await layoutDataPromise

    // if we are running in the server, acquire cache policies specified per page render
    // and set appropriate cache-control headers.
    if (magnoliaRequest) conditionallySetCacheControl(appContext, pageInitialProps)

    // if we are running in the server context, extract apollo client caches and persist
    // as props for relay to GraphClientsContainer
    const apolloCaches = magnoliaRequest
      ? extractApolloClientCache(magnoliaPageContext.apolloClientFactory)
      : undefined

    // End tracing this function.
    getInitialPropsTracingSpan?.finish()

    const requestIncludedLocalizationPathParam =
      magnoliaRequest?.headers[headerKeys.requestIncludedLocalizationPathParam] === 'true'

    const userAgent = magnoliaRequest
      ? magnoliaRequest.headers['user-agent']
      : isBrowser
      ? navigator.userAgent
      : undefined

    return {
      ...pageInitialProps,
      cookies,
      apolloCaches,
      translations,
      shopLocalizationContext: magnoliaPageContext.shopLocalizationContext,
      apolloClientFactory: magnoliaPageContext.apolloClientFactory,
      requestIncludedLocalizationPathParam,
      userAgent,
    }
  }

  private static enhanceMagnoliaPageContext(
    pageContext: NextPageContext,
    magnoliaRequest?: MagnoliaRequest
  ): MagnoliaPageContext {
    const shopLocalizationContext = runtimeSpecificShopLocalizationContext(magnoliaRequest)
    const apolloClientFactory = runtimeSpecificApolloClientFactory(magnoliaRequest)

    let magnoliaPageContext = pageContext as MagnoliaPageContext
    magnoliaPageContext.shopLocalizationContext = shopLocalizationContext
    magnoliaPageContext.apolloClientFactory = apolloClientFactory

    return magnoliaPageContext
  }

  // This function runs on the server for the initial SSR, and on the client for all renders, initial, route changes, query changes, etc.
  public render(): JSX.Element {
    const {
      pageProps,
      router,
      shopLocalizationContext,
      translations,
      cookies,
      requestIncludedLocalizationPathParam,
      userAgent,
    } = this.props

    const apolloClients = this.getApolloClients()
    this.placeSerializedInitialPropsCachesIntoClientsideCaches()

    // If we received serialized cookies from the request, use them. Otherwise we must
    // be in the browser and can instead rely on them coming from the browser apis.
    const providedCookies = cookies ? new ReactCookies(cookies) : new ReactCookies()

    const Component = this.props.Component as MagnoliaPage<MagnoliaPageInitialProps>
    const { Layout } = getLayoutConfigForPageView({
      query: router.query,
      pageComponent: Component,
    })

    return (
      <StyleSheetManager shouldForwardProp={isPropValid} enableVendorPrefixes>
        <ToplevelFigsThemeProvider>
          <GlobalStyles />
          <CookiesProvider cookies={providedCookies}>
            <ConsentPolicyProvider>
              <MediaContextProvider disableDynamicMediaQueries={true}>
                <GraphqlClientsProvider
                  type='traditional-ssr'
                  defaultClient={apolloClients[ApolloClientProfile.FigsPublicSupergraph]}
                  personalizedClient={apolloClients[ApolloClientProfile.FigsAuthedSupergraph]}
                >
                  <LocalizationProvider
                    initialState={{
                      region: supportedRegions[shopLocalizationContext.region],
                      currency: shopLocalizationContext.currency,
                      languageGroup: shopLocalizationContext.language,
                      locale: shopLocalizationContext.locale,
                      requestIncludedLocalizationPathParam,
                    }}
                  >
                    <PreviewDirectivesProvider
                      initialState={convertRequestToPreviewDirectives({
                        url: router.asPath,
                        cookies: providedCookies,
                      })}
                    >
                      <SessionProvider userAgent={userAgent}>
                        <AuthenticationProvider>
                          <ExperimentationProvider
                            initialState={undefined}
                            featureFlagOverrides={undefined} // TODO: Implement this or finish migrating off pages dir.
                          >
                            <FixturesContextProvider>
                              <TranslationProvider translations={translations}>
                                <UserProvider>
                                  <PreviewPanelProvider>
                                    <PersonalizationProvider>
                                      <TrackingProvider>
                                        <NavContainer.Provider>
                                          <FavoritesProvider rscSupported={false}>
                                            <AccentColorProvider>
                                              <EmailCaptureFormProvider>
                                                <CheckoutClientProvider>
                                                  <CartProvider>
                                                    <LoopProvider>
                                                      <OrganizationProvider>
                                                        <SearchOverlayContextProvider>
                                                          <ContextualHead />
                                                          <ScrollRestorer />

                                                          {/** Page contents for current route. */}
                                                          <Layout
                                                            content={<Component {...pageProps} />}
                                                          />
                                                          {/** Global animation for client side page transitions. */}
                                                          <Interstitial />

                                                          <PreviewPanelDialog />
                                                        </SearchOverlayContextProvider>
                                                      </OrganizationProvider>
                                                    </LoopProvider>
                                                  </CartProvider>
                                                </CheckoutClientProvider>
                                              </EmailCaptureFormProvider>
                                            </AccentColorProvider>
                                          </FavoritesProvider>
                                        </NavContainer.Provider>
                                      </TrackingProvider>
                                    </PersonalizationProvider>
                                  </PreviewPanelProvider>
                                </UserProvider>
                              </TranslationProvider>
                            </FixturesContextProvider>
                          </ExperimentationProvider>
                        </AuthenticationProvider>
                      </SessionProvider>
                    </PreviewDirectivesProvider>
                  </LocalizationProvider>
                </GraphqlClientsProvider>
              </MediaContextProvider>
            </ConsentPolicyProvider>
          </CookiesProvider>
        </ToplevelFigsThemeProvider>
      </StyleSheetManager>
    )
  }

  componentDidMount() {
    this.persistServiceFilterPairs()
  }

  /**
   * We apply a service filter request header to our GraphQL queries when
   * certain query parameters are set in Magnolia’s site URL. Persisting them
   * in memory allows us to navigate between Magnolia pages and retain the
   * desired service filter state.
   *
   * Current filters:
   * content = preview || timestamp of a contentful release
   * product-data = include-unavailable || list of launch tags
   */
  private persistServiceFilterPairs(): void {
    const ssrCookies = (this.props.cookies ?? {}) as Record<string, string>
    const browserCookies = new Cookies()
    const contentMode =
      ssrCookies[cookieKeys.contentMode.key] ?? browserCookies.get(cookieKeys.contentMode.key)
    const productData =
      ssrCookies[cookieKeys.productData.key] ?? browserCookies.get(cookieKeys.productData.key)

    ServiceFilterPairs.includeCustomerTier('web')

    if (contentMode === PREVIEW) {
      ServiceFilterPairs.includePreviewContent()
    }

    if (productData === INCLUDE_UNAVAILABLE_PRODUCTS) {
      ServiceFilterPairs.includeUnavailableProducts()
    } else if (productData) {
      ServiceFilterPairs.includeLaunchTags(productData)
    }
  }

  private getApolloClients() {
    const safeApolloClientFactory = isBrowser
      ? runtimeSpecificApolloClientFactory()
      : this.props.apolloClientFactory // apolloClientFactory can't be serialized to share with browser, so `runtimeSpecificApolloClientFactory()` must be used in browser.

    const apolloClients = {
      [ApolloClientProfile.FigsPublicSupergraph]: safeApolloClientFactory.for(
        ApolloClientProfile.FigsPublicSupergraph
      ),
      [ApolloClientProfile.FigsAuthedSupergraph]: safeApolloClientFactory.for(
        ApolloClientProfile.FigsAuthedSupergraph
      ),
    } as const

    return apolloClients
  }

  private cachesHydrated: boolean = false
  private placeSerializedInitialPropsCachesIntoClientsideCaches(): void {
    const apolloCaches = this.props.apolloCaches
    const apolloClients = this.getApolloClients()

    // Hydrate Apollo clients in browser on page load. Do not wipe them out
    // if they already have been hydrated (ex: someone calls this function after initial page load).
    if (isBrowser && apolloCaches && !this.cachesHydrated) {
      Object.entries(apolloClients).forEach(entry => {
        const apolloClientKey = entry[0]
        const apolloClient = entry[1]
        const apolloClientCacheKey = Object.keys(apolloCaches).find(apolloCacheKey =>
          apolloCacheKey.endsWith(`:${apolloClientKey}`)
        )
        const apolloClientCache = apolloClientCacheKey
          ? apolloCaches[apolloClientCacheKey]
          : undefined

        if (!apolloClientCache) return
        apolloClient.cache.restore(apolloClientCache)
      })
      this.cachesHydrated = true
    }
  }
}
